import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import * as timesheetsService from '../../timesheetsService'
import { isTimesheetsEnabled } from '../../featureRegistry'
import type { Workspace } from '../../../types/workspace'

export const TIMESHEETS_CLOCK_IN_ACTION_ID = 'timesheets.clockIn'
export const TIMESHEETS_CLOCK_OUT_ACTION_ID = 'timesheets.clockOut'
export const TIMESHEETS_START_ITEM_TIMER_ACTION_ID = 'timesheets.startItemTimer'
export const TIMESHEETS_STOP_ITEM_TIMER_ACTION_ID = 'timesheets.stopItemTimer'
export const TIMESHEETS_SET_CLIENT_RATE_ACTION_ID = 'timesheets.setClientRate'
export const TIMESHEETS_SET_TIME_ENTRY_BUCKET_ACTION_ID = 'timesheets.setTimeEntryBucket'
export const TIMESHEETS_BACKFILL_TIME_ENTRY_BUCKETS_ACTION_ID = 'timesheets.backfillTimeEntryBuckets'
export const TIMESHEETS_REVIEW_WEEK_ACTION_ID = 'timesheets.reviewWeek'
export const TIMESHEETS_REOPEN_WEEK_ACTION_ID = 'timesheets.reopenWeek'
export const TIMESHEETS_LIST_APPROVAL_QUEUE_ACTION_ID = 'timesheets.listApprovalQueue'
export const TIMESHEETS_LIST_UNPOSTED_LABOUR_ACTION_ID = 'timesheets.listUnpostedLabour'
export const TIMESHEETS_POST_PENDING_LABOUR_ACTION_ID = 'timesheets.postPendingLabour'
export const TIMESHEETS_BACKFILL_ACTUAL_MINUTES_ACTION_ID = 'timesheets.backfillActualMinutes'
export const TIMESHEETS_EXPORT_BILLABLE_HOURS_ACTION_ID = 'timesheets.exportBillableHours'
export const TIMESHEETS_EXPORT_PAYROLL_HOURS_ACTION_ID = 'timesheets.exportPayrollHours'
export const TIMESHEETS_BACKFILL_TIME_ENTRY_ZONES_ACTION_ID = 'timesheets.backfillTimeEntryZones'

export interface TimesheetsExportPayrollHoursArgs {
  workspaceId?: string
  from?: string
  to?: string
  format: 'CSV' | 'XLSX'
  uid?: string
}

export interface TimesheetsBackfillTimeEntryZonesArgs {
  workspaceId?: string
}

export interface TimesheetsExportBillableHoursArgs {
  workspaceId?: string
  clientId?: string
  from?: string
  to?: string
  format: 'CSV' | 'XLSX'
}

export interface TimesheetsListUnpostedLabourArgs {
  workspaceId?: string
}

export interface TimesheetsPostPendingLabourArgs {
  workspaceId?: string
  uid?: string
  weekIds?: string[]
}

export interface TimesheetsBackfillActualMinutesArgs {
  workspaceId?: string
}

export interface TimesheetsClockInArgs {
  workspaceId?: string
  nodeId?: string
  kind?: 'CLOCK' | 'TIMER'
}

export interface TimesheetsClockOutArgs {
  workspaceId?: string
  note?: string
}

export interface TimesheetsStartItemTimerArgs {
  workspaceId?: string
  nodeId: string
  nodeLabel?: string
}

export interface TimesheetsStopItemTimerArgs {
  workspaceId?: string
  note?: string
}

export interface TimesheetsSetClientRateArgs {
  workspaceId?: string
  clientId: string
  rate: number
  currency: string
  effectiveFrom: string
}

export interface TimesheetsSetTimeEntryBucketArgs {
  workspaceId?: string
  entryId: string
  bucket: 'CLIENT_BILLABLE' | 'FIXED_BID' | 'CLIENT_NON_BILLABLE' | 'INTERNAL'
}

export interface TimesheetsBackfillTimeEntryBucketsArgs {
  workspaceId?: string
  limit?: number
  cursor?: string
}

export interface TimesheetsReviewWeekArgs {
  workspaceId?: string
  targetUid: string
  weekId: string
  decision: 'APPROVE' | 'REJECT'
  note?: string
}

export interface TimesheetsReopenWeekArgs {
  workspaceId?: string
  targetUid: string
  weekId: string
  reason: string
}

export interface TimesheetsListApprovalQueueArgs {
  workspaceId?: string
  status?: 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  weekId?: string
  cursor?: string
  limit?: number
}

const clockInSchema: JSONSchemaType<TimesheetsClockInArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    nodeId: { type: 'string', nullable: true },
    kind: { type: 'string', enum: ['CLOCK', 'TIMER'], nullable: true },
  },
  additionalProperties: false,
}

const clockOutSchema: JSONSchemaType<TimesheetsClockOutArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    note: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

const startItemTimerSchema: JSONSchemaType<TimesheetsStartItemTimerArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    nodeId: { type: 'string' },
    nodeLabel: { type: 'string', nullable: true },
  },
  required: ['nodeId'],
  additionalProperties: false,
}

const stopItemTimerSchema: JSONSchemaType<TimesheetsStopItemTimerArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    note: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

const setClientRateSchema: JSONSchemaType<TimesheetsSetClientRateArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string' },
    rate: { type: 'number' },
    currency: { type: 'string' },
    effectiveFrom: { type: 'string' },
  },
  required: ['clientId', 'rate', 'currency', 'effectiveFrom'],
  additionalProperties: false,
}

const setTimeEntryBucketSchema: JSONSchemaType<TimesheetsSetTimeEntryBucketArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    entryId: { type: 'string' },
    bucket: { type: 'string', enum: ['CLIENT_BILLABLE', 'FIXED_BID', 'CLIENT_NON_BILLABLE', 'INTERNAL'] },
  },
  required: ['entryId', 'bucket'],
  additionalProperties: false,
}

const backfillTimeEntryBucketsSchema: JSONSchemaType<TimesheetsBackfillTimeEntryBucketsArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    limit: { type: 'number', nullable: true },
    cursor: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

const reviewWeekActionSchema: JSONSchemaType<TimesheetsReviewWeekArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    targetUid: { type: 'string' },
    weekId: { type: 'string' },
    decision: { type: 'string', enum: ['APPROVE', 'REJECT'] },
    note: { type: 'string', nullable: true },
  },
  required: ['targetUid', 'weekId', 'decision'],
  additionalProperties: false,
}

const reopenWeekActionSchema: JSONSchemaType<TimesheetsReopenWeekArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    targetUid: { type: 'string' },
    weekId: { type: 'string' },
    reason: { type: 'string' },
  },
  required: ['targetUid', 'weekId', 'reason'],
  additionalProperties: false,
}

const listApprovalQueueActionSchema: JSONSchemaType<TimesheetsListApprovalQueueArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    status: { type: 'string', enum: ['SUBMITTED', 'APPROVED', 'REJECTED'], nullable: true },
    weekId: { type: 'string', nullable: true },
    cursor: { type: 'string', nullable: true },
    limit: { type: 'number', nullable: true },
  },
  additionalProperties: false,
}

const listUnpostedLabourSchema: JSONSchemaType<TimesheetsListUnpostedLabourArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

const postPendingLabourSchema: JSONSchemaType<TimesheetsPostPendingLabourArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    uid: { type: 'string', nullable: true },
    weekIds: {
      type: 'array',
      items: { type: 'string' },
      nullable: true,
    },
  },
  additionalProperties: false,
}

const backfillActualMinutesSchema: JSONSchemaType<TimesheetsBackfillActualMinutesArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

const exportBillableHoursSchema: JSONSchemaType<TimesheetsExportBillableHoursArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string', nullable: true },
    from: { type: 'string', nullable: true },
    to: { type: 'string', nullable: true },
    format: { type: 'string', enum: ['CSV', 'XLSX'] },
  },
  required: ['format'],
  additionalProperties: false,
}

const exportPayrollHoursSchema: JSONSchemaType<TimesheetsExportPayrollHoursArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    from: { type: 'string', nullable: true },
    to: { type: 'string', nullable: true },
    format: { type: 'string', enum: ['CSV', 'XLSX'] },
    uid: { type: 'string', nullable: true },
  },
  required: ['format'],
  additionalProperties: false,
}

const backfillTimeEntryZonesSchema: JSONSchemaType<TimesheetsBackfillTimeEntryZonesArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
}

const resolveWorkspace = (context: { workspaceId?: string; switchedOffModules?: ReadonlySet<string> }) => ({
  id: context.workspaceId ?? 'default-ws',
  plan: 'ESSENTIAL_TIER',
  modules: {
    timesheetsEnabled: !context.switchedOffModules?.has('timesheets'),
  },
} as unknown as Workspace)

const clockInAction: ActionDefinition<TimesheetsClockInArgs, unknown> = {
  id: TIMESHEETS_CLOCK_IN_ACTION_ID,
  label: 'Clock in',
  description: 'Clocks in or starts a timer for the current user.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: clockInSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-clock-in'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.startClock({
        workspaceId,
        nodeId: args.nodeId ?? null,
        kind: args.kind ?? 'CLOCK',
      })
      return {
        kind: 'COMPLETED',
        message: 'Clocked in successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const clockOutAction: ActionDefinition<TimesheetsClockOutArgs, unknown> = {
  id: TIMESHEETS_CLOCK_OUT_ACTION_ID,
  label: 'Clock out',
  description: 'Clocks out the current running clock.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: clockOutSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-clock-out'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.stopClock({
        workspaceId,
        note: args.note ?? null,
      })
      return {
        kind: 'COMPLETED',
        message: 'Clocked out successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const stopItemTimerAction: ActionDefinition<TimesheetsStopItemTimerArgs, unknown> = {
  id: TIMESHEETS_STOP_ITEM_TIMER_ACTION_ID,
  label: 'Stop item timer',
  description: 'Stops the running item timer.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: stopItemTimerSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-stop-item-timer'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.stopClock({
        workspaceId,
        note: args.note ?? null,
      })
      return {
        kind: 'COMPLETED',
        message: 'Timer stopped successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const startItemTimerAction: ActionDefinition<TimesheetsStartItemTimerArgs, unknown> = {
  id: TIMESHEETS_START_ITEM_TIMER_ACTION_ID,
  label: 'Start item timer',
  description: 'Starts a timer on a specific item.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: startItemTimerSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-start-item-timer'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.startClock({
        workspaceId,
        nodeId: args.nodeId,
        kind: 'TIMER',
      })
      return {
        kind: 'COMPLETED',
        message: 'Timer started successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const setClientRateAction: ActionDefinition<TimesheetsSetClientRateArgs, unknown> = {
  id: TIMESHEETS_SET_CLIENT_RATE_ACTION_ID,
  label: 'Set client rate',
  description: 'Sets the default bill rate for a client.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: setClientRateSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-set-client-rate'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.setClientRate({
        workspaceId,
        clientId: args.clientId,
        rate: args.rate,
        currency: args.currency,
        effectiveFrom: args.effectiveFrom,
      })
      return {
        kind: 'COMPLETED',
        message: 'Client rate updated successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const setTimeEntryBucketAction: ActionDefinition<TimesheetsSetTimeEntryBucketArgs, unknown> = {
  id: TIMESHEETS_SET_TIME_ENTRY_BUCKET_ACTION_ID,
  label: 'Override time entry bucket',
  description: 'Overrides the stored billing bucket of a time entry.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: setTimeEntryBucketSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-set-time-entry-bucket'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.setTimeEntryBucket({
        workspaceId,
        entryId: args.entryId,
        bucket: args.bucket,
      })
      return {
        kind: 'COMPLETED',
        message: 'Time entry bucket overridden successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const backfillTimeEntryBucketsAction: ActionDefinition<TimesheetsBackfillTimeEntryBucketsArgs, unknown> = {
  id: TIMESHEETS_BACKFILL_TIME_ENTRY_BUCKETS_ACTION_ID,
  label: 'Backfill time entry buckets',
  description: 'Backfills missing buckets on existing time entries.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: backfillTimeEntryBucketsSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-backfill-time-entry-buckets'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.backfillTimeEntryBuckets({
        workspaceId,
        limit: args.limit,
        cursor: args.cursor,
      })
      return {
        kind: 'COMPLETED',
        message: 'Time entry buckets backfilled successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const reviewWeekAction: ActionDefinition<TimesheetsReviewWeekArgs, unknown> = {
  id: TIMESHEETS_REVIEW_WEEK_ACTION_ID,
  label: 'Review timesheet week',
  description: 'Approves or rejects a submitted timesheet week.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: reviewWeekActionSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-review-week'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.reviewWeek({
        workspaceId,
        targetUid: args.targetUid,
        weekId: args.weekId,
        decision: args.decision,
        note: args.note,
      })
      return {
        kind: 'COMPLETED',
        message: args.decision === 'APPROVE' ? 'Timesheet approved.' : 'Timesheet rejected.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const reopenWeekAction: ActionDefinition<TimesheetsReopenWeekArgs, unknown> = {
  id: TIMESHEETS_REOPEN_WEEK_ACTION_ID,
  label: 'Reopen timesheet week',
  description: 'Reopens an approved timesheet week for revision.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: reopenWeekActionSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-reopen-week'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.reopenWeek({
        workspaceId,
        targetUid: args.targetUid,
        weekId: args.weekId,
        reason: args.reason,
      })
      return {
        kind: 'COMPLETED',
        message: 'Timesheet week reopened successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const listApprovalQueueAction: ActionDefinition<TimesheetsListApprovalQueueArgs, unknown> = {
  id: TIMESHEETS_LIST_APPROVAL_QUEUE_ACTION_ID,
  label: 'List timesheets approval queue',
  description: 'Retrieves timesheets awaiting review or in approved/rejected states.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: listApprovalQueueActionSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-list-approval-queue'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.listApprovalQueue({
        workspaceId,
        status: args.status,
        weekId: args.weekId,
        cursor: args.cursor,
        limit: args.limit,
      })
      return {
        kind: 'COMPLETED',
        message: 'Approval queue retrieved successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const listUnpostedLabourAction: ActionDefinition<TimesheetsListUnpostedLabourArgs, unknown> = {
  id: TIMESHEETS_LIST_UNPOSTED_LABOUR_ACTION_ID,
  label: 'List unposted labour weeks',
  description: 'Lists approved timesheet weeks that had no cost rate at approval time.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: listUnpostedLabourSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-list-unposted-labour'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.listUnpostedLabour({ workspaceId })
      return {
        kind: 'COMPLETED',
        message: 'Unposted labour retrieved successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const postPendingLabourAction: ActionDefinition<TimesheetsPostPendingLabourArgs, unknown> = {
  id: TIMESHEETS_POST_PENDING_LABOUR_ACTION_ID,
  label: 'Post pending labour costs',
  description: 'Posts labour expenses to budget for approved weeks that now have rates.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: postPendingLabourSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-post-pending-labour'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.postPendingLabour({
        workspaceId,
        uid: args.uid,
        weekIds: args.weekIds,
      })
      return {
        kind: 'COMPLETED',
        message: 'Pending labour posted successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const backfillActualMinutesAction: ActionDefinition<TimesheetsBackfillActualMinutesArgs, unknown> = {
  id: TIMESHEETS_BACKFILL_ACTUAL_MINUTES_ACTION_ID,
  label: 'Backfill actual minutes',
  description: 'Recomputes actualMinutes roll-up on all elements and checklists from approved entries.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: backfillActualMinutesSchema,
  permission: (context) => isTimesheetsEnabled(resolveWorkspace(context)),
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-backfill-actual-minutes'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.backfillActualMinutes({ workspaceId })
      return {
        kind: 'COMPLETED',
        message: 'Actual minutes backfilled successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const exportBillableHoursAction: ActionDefinition<TimesheetsExportBillableHoursArgs, unknown> = {
  id: TIMESHEETS_EXPORT_BILLABLE_HOURS_ACTION_ID,
  label: 'Export billable hours',
  description: 'Exports approved billable hours and revenue breakdowns for a workspace or client.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: exportBillableHoursSchema,
  permission: (context) => {
    if (!isTimesheetsEnabled(resolveWorkspace(context))) return false
    const role = (context.actor?.role || '').toUpperCase()
    return role === 'ADMIN' || role === 'OWNER' || role === 'EDITOR'
  },
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-export-billable-hours'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.exportBillableHours({
        workspaceId,
        clientId: args.clientId,
        from: args.from,
        to: args.to,
        format: args.format,
      })
      return {
        kind: 'COMPLETED',
        message: 'Billable hours exported successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const exportPayrollHoursAction: ActionDefinition<TimesheetsExportPayrollHoursArgs, unknown> = {
  id: TIMESHEETS_EXPORT_PAYROLL_HOURS_ACTION_ID,
  label: 'Export payroll hours',
  description: 'Exports approved and locked hours with regular and overtime breakdown and pay calculations.',
  risk: 'LOW',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: exportPayrollHoursSchema,
  permission: (context) => {
    if (!isTimesheetsEnabled(resolveWorkspace(context))) return false
    const role = (context.actor?.role || '').toUpperCase()
    return role === 'ADMIN' || role === 'OWNER'
  },
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-export-payroll-hours'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.exportPayrollHours({
        workspaceId,
        from: args.from,
        to: args.to,
        format: args.format,
        uid: args.uid,
      })
      return {
        kind: 'COMPLETED',
        message: 'Payroll hours exported successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const backfillTimeEntryZonesAction: ActionDefinition<TimesheetsBackfillTimeEntryZonesArgs, unknown> = {
  id: TIMESHEETS_BACKFILL_TIME_ENTRY_ZONES_ACTION_ID,
  label: 'Backfill time entry time zones',
  description: 'Stamps workspace time zone on entries lacking tz and recomputes week IDs for draft and rejected entries.',
  risk: 'MEDIUM',
  domain: 'timesheets',
  surfaces: ['catalog'],
  argsSchema: backfillTimeEntryZonesSchema,
  permission: (context) => {
    if (!isTimesheetsEnabled(resolveWorkspace(context))) return false
    const role = (context.actor?.role || '').toUpperCase()
    return role === 'ADMIN' || role === 'OWNER'
  },
  run: async (args, context): Promise<ActionOutcome<unknown>> => {
    const correlationId = context.correlationId ?? 'timesheets-backfill-time-entry-zones'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for timesheet operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await timesheetsService.backfillTimeEntryZones({ workspaceId })
      return {
        kind: 'COMPLETED',
        message: 'Time entry zones backfilled successfully.',
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

export const registerTimesheetsActions = (): void => {
  if (!getAction(TIMESHEETS_CLOCK_IN_ACTION_ID)) {
    registerAction(clockInAction)
  }
  if (!getAction(TIMESHEETS_CLOCK_OUT_ACTION_ID)) {
    registerAction(clockOutAction)
  }
  if (!getAction(TIMESHEETS_START_ITEM_TIMER_ACTION_ID)) {
    registerAction(startItemTimerAction)
  }
  if (!getAction(TIMESHEETS_STOP_ITEM_TIMER_ACTION_ID)) {
    registerAction(stopItemTimerAction)
  }
  if (!getAction(TIMESHEETS_SET_CLIENT_RATE_ACTION_ID)) {
    registerAction(setClientRateAction)
  }
  if (!getAction(TIMESHEETS_SET_TIME_ENTRY_BUCKET_ACTION_ID)) {
    registerAction(setTimeEntryBucketAction)
  }
  if (!getAction(TIMESHEETS_BACKFILL_TIME_ENTRY_BUCKETS_ACTION_ID)) {
    registerAction(backfillTimeEntryBucketsAction)
  }
  if (!getAction(TIMESHEETS_REVIEW_WEEK_ACTION_ID)) {
    registerAction(reviewWeekAction)
  }
  if (!getAction(TIMESHEETS_REOPEN_WEEK_ACTION_ID)) {
    registerAction(reopenWeekAction)
  }
  if (!getAction(TIMESHEETS_LIST_APPROVAL_QUEUE_ACTION_ID)) {
    registerAction(listApprovalQueueAction)
  }
  if (!getAction(TIMESHEETS_LIST_UNPOSTED_LABOUR_ACTION_ID)) {
    registerAction(listUnpostedLabourAction)
  }
  if (!getAction(TIMESHEETS_POST_PENDING_LABOUR_ACTION_ID)) {
    registerAction(postPendingLabourAction)
  }
  if (!getAction(TIMESHEETS_BACKFILL_ACTUAL_MINUTES_ACTION_ID)) {
    registerAction(backfillActualMinutesAction)
  }
  if (!getAction(TIMESHEETS_EXPORT_BILLABLE_HOURS_ACTION_ID)) {
    registerAction(exportBillableHoursAction)
  }
  if (!getAction(TIMESHEETS_EXPORT_PAYROLL_HOURS_ACTION_ID)) {
    registerAction(exportPayrollHoursAction)
  }
  if (!getAction(TIMESHEETS_BACKFILL_TIME_ENTRY_ZONES_ACTION_ID)) {
    registerAction(backfillTimeEntryZonesAction)
  }
}

registerTimesheetsActions()
